/**
 * Lokalny cache umówionych wizyt (localStorage).
 *
 * Po udanej rejestracji zapisujemy całą wizytę - dane usługi/terminu, dane
 * pacjenta oraz odpowiedź Medfile (linki do pokoju, link do anulowania,
 * link do płatności). Dzięki temu na profilu specjalisty, pod kalendarzem,
 * możemy pokazać ostatnio umówione wizyty razem z linkiem do odwołania.
 *
 * Trzymamy jedną listę wpisów pod stałym kluczem; każdy wpis wie, do którego
 * specjalisty (slug) należy, więc widok specjalisty filtruje po slugu.
 */

const STORAGE_KEY = 'code:bookings:v1';
const MAX_ENTRIES = 50;

function isBrowser() {
  return typeof window !== 'undefined' && !!window.localStorage;
}

function readAll() {
  if (!isBrowser()) return [];

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeAll(entries) {
  if (!isBrowser()) return;

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(entries.slice(0, MAX_ENTRIES)));
  } catch {
    // Prywatny tryb / brak miejsca - ignorujemy, cache jest tylko wygodą.
  }
}

function randomId() {
  if (isBrowser() && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  return `b_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function bookingRecencyTs(booking) {
  const candidates = [
    booking?.savedAt,
    booking?.output?.visit?.start,
    booking?.chosenDate,
  ];

  for (const value of candidates) {
    if (!value) continue;
    const ts = Date.parse(value);
    if (!Number.isNaN(ts)) return ts;
  }

  return 0;
}

/** Najnowsze wpisy pierwsze (po dacie zapisu, potem terminie wizyty). */
function sortNewestFirst(entries) {
  return [...entries].sort((a, b) => bookingRecencyTs(b) - bookingRecencyTs(a));
}

/**
 * Zapisuje wizytę do cache i zwraca zapisany wpis.
 * @param {object} booking - pełne dane wizyty (patrz Registration.vue).
 */
export function saveBooking(booking) {
  const entry = {
    ...booking,
    id: booking.id || randomId(),
    // Always refresh — must win over any savedAt coming from ...booking.
    savedAt: new Date().toISOString(),
  };

  const entries = readAll().filter((item) => item.id !== entry.id);
  entries.unshift(entry);
  writeAll(entries);

  return entry;
}

/** Wszystkie zapisane wizyty (najnowsze pierwsze). */
export function getBookings() {
  return sortNewestFirst(readAll());
}

/** Pojedyncza wizyta po id (id z URL /rejestracja/:id). */
export function getBooking(id) {
  if (!id) return null;
  return readAll().find((item) => item.id === id) || null;
}

/** Wizyty umówione u danego specjalisty (po slugu), najnowsze pierwsze. */
export function getBookingsForSpecialist(specialistSlug) {
  if (!specialistSlug) return [];
  return sortNewestFirst(
    readAll().filter((item) => item.specialistSlug === specialistSlug),
  );
}

/** Usuwa wpis z cache (np. po ręcznym „ukryciu” przez pacjenta). */
export function removeBooking(id) {
  writeAll(readAll().filter((item) => item.id !== id));
}

/** Nadpisuje istniejące wpisy świeżymi danymi z API (po id). */
export function mergeBookings(bookings) {
  if (!Array.isArray(bookings) || bookings.length === 0) return readAll();

  const byId = new Map(bookings.map((booking) => [booking.id, booking]));
  const entries = readAll().map((entry) => {
    const fresh = byId.get(entry.id);
    if (!fresh) return entry;
    return {
      ...entry,
      ...fresh,
      savedAt: entry.savedAt || new Date().toISOString(),
    };
  });

  writeAll(entries);
  return entries;
}
